/**
 * Adapters between LabInvestigationForm's Lab v2 body shape (camelCase,
 * sent to api-lab-investigation-v2-service today) and the transcript's
 * captured-item payload shape (snake_case, matches what the Python agent
 * emits and what Go's SaveSection fan-out reads on Save & next).
 *
 * Same pattern as complaintBodyAdapters / diagnosisBodyAdapters /
 * medicineBodyAdapters / hospitalisationBodyAdapters. One captured item
 * = one Investigation sub-test row (Lab v2's Add path is 1:1).
 *
 *   Lab v2 add body        → captured "add" payload
 *   {test:{sltCode, ltCode, loincCode, name, panel, unit, refRange},
 *    testDate, value, numericValue?, labName, source, description}
 *      →
 *   {test_name, investigation_panel, result_value, result_units,
 *    ref_range (text), test_date, loinc_code, description}
 *
 * Simplifications:
 *   - `sltCode` / `ltCode` are Zyla-internal catalog codes; the AI
 *     never has them. They stay on the captured payload only when the
 *     manual card picked a catalog match (so the round-trip preserves
 *     them), but AI-added rows always ship empty. Reviewer picks a
 *     catalog match on standalone edit if needed.
 *   - `refRange` collapses to its `text` field on the round-trip. The
 *     structured `low/high` fields (only set today by the catalog for
 *     panels with numeric bands) are preserved by the standalone
 *     re-save path; the transcript round-trip drops them (they can be
 *     re-derived from `text` server-side when the flag needs computing).
 *   - `labName` (which lab reported the result) has no AI slot; empty.
 */

const toIsoDate = (iso) => (typeof iso === "string" && iso ? iso.slice(0, 10) : "");

// Flatten refRange to a human string — same rendering the standalone
// form uses so a round-trip preserves what the user saw.
const refRangeToText = (r) => {
  if (!r) return "";
  if (r.low != null && r.high != null) return `${r.low}–${r.high}`;
  if (r.low != null) return `≥ ${r.low}`;
  if (r.high != null) return `≤ ${r.high}`;
  return r.text || "";
};

/**
 * Lab v2 add body → captured "add" payload.
 */
export function bodyToCapturedAdd(body) {
  const t = body.test || {};
  const aptId = Number(body.appointmentId);
  const out = {
    test_name:           t.name || "",
    investigation_panel: t.panel || "",
    result_value:        body.value || "",
    result_units:        t.unit || "",
    ref_range:           refRangeToText(t.refRange),
    test_date:           toIsoDate(body.testDate),
    loinc_code:          t.loincCode || "",
    loinc_description:   "",
    description:         body.description || "",
  };
  // Only stamp appointmentId when the caller had one — omit for AI-
  // originated captures so a Save & next without a review edit doesn't
  // silently write 0 downstream.
  if (Number.isFinite(aptId) && aptId > 0) {
    out.appointment_id = aptId;
  }
  return out;
}

/**
 * Stub — Wave A pass 1 doesn't route Lab updates via AI. Symmetric
 * with the other section adapters.
 */
export function bodyToCapturedUpdate(body /* , context */) {
  return bodyToCapturedAdd(body);
}

/* -------------------------------------------------------------------
 * DigitiseManuallyCard reconciliation
 * -----------------------------------------------------------------
 * The transcript-mode LI review renders one card per unique
 * (Lab test, Test date) combination. Each card holds N sub-tests;
 * on Add, the card commits to the review store as N captured items
 * (single-shape, one per sub-test — same wire shape as the AI's
 * agent proposals). The helpers below convert between the card's
 * grouped shape and the review store's flat items.
 * ------------------------------------------------------------- */

// Group identity for a captured item — collapses sub-test rows that
// belong to the same parent test taken on the same date into one
// card. investigation_panel is present when the parent test has a
// subtest corpus (its sub-tests each carry the parent name in
// `panel`); empty otherwise (the row IS the parent test — no sub-
// test corpus). Fallback to test_name keeps parent-as-sub rows
// unambiguous.
export const groupKey = (item) => {
  const p = (item && item.current) || item || {};
  const panel = (p.investigation_panel || "").trim();
  const name = (p.test_name || "").trim();
  const date = (p.test_date || "").slice(0, 10);
  return `${panel || name}||${date}`;
};

// Bucket a flat list of captured items into their DigitiseManuallyCard
// groups. Preserves original order within a group. Returns
// [{key, items:[capturedItem, ...]}] so callers can iterate + render
// per-card without re-computing keys.
export const groupCapturedByTestDate = (items) => {
  const byKey = new Map();
  const order = [];
  (items || []).forEach((it) => {
    const k = groupKey(it);
    if (!byKey.has(k)) {
      byKey.set(k, []);
      order.push(k);
    }
    byKey.get(k).push(it);
  });
  return order.map((k) => ({ key: k, items: byKey.get(k) }));
};

// Reverse the flat-payload → card-editing shape so DigitiseManuallyCard
// can seed itself from a group. Reads the FIRST item's identity for the
// parent labTest (all items in the group share it by construction) and
// maps each sub-test row from its own payload.
//
// The reconstructed labTest is a minimal object — enough for TestCard's
// initial render (name + code + loinc). The catalog fetch fills in the
// full record on mount, at which point picking / reassigning goes
// through the fresh catalog data.
export const capturedItemsToCardEditing = (items) => {
  if (!items || items.length === 0) return null;
  const first = (items[0].current || items[0]) || {};
  const panel = (first.investigation_panel || "").trim();
  const parentName = panel || first.test_name || "";
  // Prime `subtest[]` from the captured items themselves — when the
  // parent has a corpus, the reviewer's current picks ARE the corpus
  // until the catalog fetch resolves the full record on card mount.
  // The card's own catalog effect then replaces this stub with the
  // real record so the picker dropdown carries every option upstream.
  const primedSubtestCorpus = panel
    ? items.map((it) => {
        const p = it.current || it;
        return {
          sltCode:      p.slt_code || "",
          subtestName:  p.test_name || "",
          loincCode:    p.loinc_code || "",
        };
      }).filter((s) => s.sltCode || s.subtestName)
    : null;
  const labTest = {
    testName:  parentName,
    ltCode:    first.parent_lt_code || "",
    loincCode: panel ? "" : (first.loinc_code || ""),
    subtest:   primedSubtestCorpus,
  };
  const subTests = items.map((it) => {
    const p = it.current || it;
    return {
      id:  it.id || undefined,
      sub: (p.test_name && panel)
        ? {
            sltCode:     p.slt_code || "",
            subtestName: p.test_name || "",
            loincCode:   p.loinc_code || "",
          }
        : null,
      // Leave blank when the captured payload has no description — the
      // card's loinc-backfill effect fetches the proper name on mount via
      // getSubTestOnSelect. Falling back to loinc_code here would show the
      // code (e.g. "40754-4") in the LOINC name field until the reviewer
      // re-selects the Sub test.
      loincName: p.loinc_description || "",
      value:     p.result_value || "",
      unit:      p.result_units || "",
      range:     p.flag || p.range || "",
      // Optional qualifier — the SubTestCard shows the Tag dropdown
      // only when the picked sub-test's sltCode has a curated tag
      // list. Restored here so a re-open keeps the reviewer's pick.
      tag:       p.tag || "",
    };
  });
  return {
    labTest,
    testDate: (first.test_date || "").slice(0, 10),
    subTests,
  };
};

// Convert a DigitiseManuallyCard commit ({labTest, testDate, subTests[]})
// into the flat captured-payload shape used everywhere else in the
// transcript store. One captured payload per sub-test row. Mirrors the
// per-row TestSnapshot logic in DigitiseManuallyCard.buildBatchBody so
// the round-trip stays lossless.
export const cardCommitToCapturedItems = (commit) => {
  if (!commit || !commit.labTest) return [];
  const parent = commit.labTest;
  const hasCorpus = Array.isArray(parent.subtest) && parent.subtest.length > 0;
  return (commit.subTests || []).map((s) => {
    const rowName = hasCorpus
      ? ((s.sub && s.sub.subtestName) || "")
      : (parent.testName || "");
    const rowLoinc = hasCorpus
      ? ((s.sub && s.sub.loincCode) || "")
      : (parent.loincCode || "");
    const rowSltCode = hasCorpus ? ((s.sub && s.sub.sltCode) || "") : "";
    return {
      test_name:           rowName,
      investigation_panel: hasCorpus ? (parent.testName || "") : "",
      // parent_lt_code is not part of the Lab v2 wire body — it's an
      // internal marker so a later card-open can rebuild the labTest
      // shell without a catalog lookup.
      parent_lt_code:      parent.ltCode || "",
      slt_code:            rowSltCode,
      result_value:        String(s.value || "").trim(),
      result_units:        s.unit || "",
      ref_range:           "",
      test_date:           commit.testDate || "",
      loinc_code:          rowLoinc,
      loinc_description:   s.loincName || "",
      // flag ↔ the standalone modal's `flag` field on the BatchEntry
      // (row-level Range classification: none / normal / abnormal /
      // high / medium / low). Preserved separately from range so the
      // capture round-trip survives a re-open.
      flag:                s.range || "",
      // Qualifier tag — only meaningful when the picked sub-test's
      // sltCode has a curated tag list (e.g. Morning / Evening BP,
      // PP1 / PP2 / PP3 blood sugar). Empty for everything else.
      tag:                 s.tag || "",
      description:         "",
    };
  });
};

// Convert a DigitiseManuallyCard commit into the single captured-shape
// payload the existing save-XXX-lab hooks expect. Bundles the primary
// (first) sub-test's fields into a body that mimics LabInvestigationForm's
// output, then hands it to bodyToCapturedAdd. Any extra sub-tests ride
// through on `_extra_sub_tests` as a Phase-2 hook for the multi-item
// fan-out — the current per-item save path only reads the primary row.
export const commitToPrimaryCapturedAdd = (commit, appointmentId) => {
  const payloads = cardCommitToCapturedItems(commit) || [];
  if (payloads.length === 0) return null;
  const primary = payloads[0];
  const body = {
    test: {
      sltCode:   primary.slt_code || "",
      ltCode:    primary.parent_lt_code || "",
      loincCode: primary.loinc_code || "",
      name:      primary.test_name || "",
      panel:     primary.investigation_panel || "",
      unit:      primary.result_units || "",
      refRange:  { text: primary.ref_range || "" },
    },
    testDate:      primary.test_date || "",
    value:         primary.result_value || "",
    description:   primary.description || "",
    appointmentId: appointmentId || 0,
  };
  const converted = bodyToCapturedAdd(body);
  if (payloads.length > 1) converted._extra_sub_tests = payloads.slice(1);
  if (primary.flag) converted.flag = primary.flag;
  if (primary.parent_lt_code) converted.parent_lt_code = primary.parent_lt_code;
  if (primary.slt_code) converted.slt_code = primary.slt_code;
  if (primary.tag) converted.tag = primary.tag;
  return converted;
};
